"use client";

/**
 * `NumberField` — single-line numeric input (`<input type="number">`).
 * The form-state value is a real JavaScript `number` (not a string), so
 * downstream consumers (zod refinements, formatters, totals) can do math
 * directly on `getValues().qty` without a parseFloat round-trip.
 *
 * The `valueAsNumber: true` option on rhf's `register` is what gives us
 * the parsed-on-change behaviour. We still set HTML `min`/`max`/`step`
 * for browser-level input affordances, but actual validation happens
 * via the zod schema.
 *
 * Edge case worth knowing: when an empty native number input bubbles up
 * with `valueAsNumber`, rhf records `NaN`, not the empty string. zod
 * schemas should declare `z.number()` (which already rejects NaN) or use
 * a `.transform(Number.isNaN(v) ? null : v)` to normalise to null. We
 * intentionally do NOT normalise inside the field — the engine stays
 * out of zod's domain.
 */

import * as React from "react";
import { Input } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { NumberFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface NumberFieldProps {
  field: NumberFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function NumberField({
  field,
  pathPrefix,
}: NumberFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Input
        id={path}
        type="number"
        aria-invalid={errorMessage !== undefined ? true : undefined}
        {...(field.disabled ? { disabled: true } : {})}
        {...(field.placeholder !== undefined
          ? { placeholder: field.placeholder }
          : {})}
        {...(field.min !== undefined ? { min: field.min } : {})}
        {...(field.max !== undefined ? { max: field.max } : {})}
        {...(field.step !== undefined ? { step: field.step } : {})}
        {...form.register(path, { valueAsNumber: true })}
      />
    </FieldShell>
  );
}
